/**
 * Resources als Sammelbereich (F-17) - rein und getestet.
 *
 * Eigene Notizen und gemerkte Gedanken aus Journal, Reflexionen und Wissen
 * stehen in einer Liste. Es bleiben zwei Tabellen (derselbe Gedanke wie im
 * Journal, keine Kopie); hier werden sie nur zu einem gemeinsamen Eintrag
 * zusammengelegt.
 *
 * Zuordnung nach der Regel aus F-11: Projekt ODER Ziel, der Bereich folgt dem
 * Elternteil. Ohne Bereich, Ziel und Projekt ist ein Eintrag "unzugeordnet" -
 * genau dafuer ist Resources der Sammelplatz.
 */
import { normalizeTag } from '@/domain/resources/search';
import type { Goal, ID, ISOInstant, Project, Resource, Thought } from '@/domain/types';

export type Herkunft = 'eigen' | 'journal' | 'reflexion' | 'wissen' | 'schule' | 'lesezeichen';

export const HERKUNFT: { key: Herkunft; label: string }[] = [
  { key: 'eigen', label: 'Eigene Notiz' },
  { key: 'journal', label: 'Journal' },
  { key: 'reflexion', label: 'Reflexion' },
  { key: 'wissen', label: 'Wissen' },
  { key: 'schule', label: 'Schule' },
  { key: 'lesezeichen', label: 'Lesezeichen' },
];

export const herkunftLabel = (h: Herkunft) => HERKUNFT.find((x) => x.key === h)!.label;

export interface Zuordnung {
  areaId: ID | null;
  projectId?: ID;
  goalId?: ID;
}

export interface SammelEintrag extends Zuordnung {
  art: 'notiz' | 'gedanke';
  id: ID;
  titel: string;
  text: string;
  tags: string[];
  herkunft: Herkunft;
  angeheftet: boolean;
  /** Notiz im Archiv bzw. Gedanke erledigt/verworfen. */
  archiviert: boolean;
  /** Schul-Mitschrift (F-22) - fuer den Link zum Fach. */
  subjectId?: ID;
  geaendert: ISOInstant;
}

export function ausNotiz(r: Resource): SammelEintrag {
  return {
    art: 'notiz',
    id: r.id,
    titel: r.title,
    text: [r.body ?? '', r.url ?? ''].filter(Boolean).join('\n'),
    tags: r.tags,
    herkunft: r.subjectId ? 'schule' : r.kind === 'link' ? 'lesezeichen' : 'eigen',
    angeheftet: r.pinned === 1,
    archiviert: r.bucket === 'archiv',
    geaendert: r.updatedAt,
    ...(r.subjectId ? { subjectId: r.subjectId } : {}),
    areaId: r.areaId,
    ...(r.projectId ? { projectId: r.projectId } : {}),
    ...(r.goalId ? { goalId: r.goalId } : {}),
  };
}

export function ausGedanke(g: Thought): SammelEintrag {
  const herkunft: Herkunft = !g.quelle ? 'journal' : g.quelle.art === 'wissen' ? 'wissen' : 'reflexion';
  return {
    art: 'gedanke',
    id: g.id,
    titel: g.text,
    text: g.note ?? '',
    tags: [],
    herkunft,
    angeheftet: false,
    archiviert: g.status === 'erledigt',
    geaendert: g.updatedAt,
    areaId: g.areaId ?? null,
    ...(g.projectId ? { projectId: g.projectId } : {}),
    ...(g.goalId ? { goalId: g.goalId } : {}),
  };
}

export const istZugeordnet = (e: Zuordnung) => Boolean(e.areaId || e.projectId || e.goalId);

export interface SammelFilter {
  zuordnung: 'alle' | 'unzugeordnet';
  herkunft?: Herkunft;
  tag?: string;
  archivierte: boolean;
  text?: string;
}

/** Filter ausser der Herkunft - Grundlage fuer die Zahlen an den Herkunfts-Knoepfen. */
function passtOhneHerkunft(e: SammelEintrag, f: SammelFilter): boolean {
  if (f.zuordnung === 'unzugeordnet' && istZugeordnet(e)) return false;
  if (!f.archivierte && e.archiviert) return false;
  if (f.tag && !e.tags.some((t) => normalizeTag(t) === normalizeTag(f.tag!))) return false;
  const text = f.text?.trim().toLocaleLowerCase('de');
  if (text && ![e.titel, e.text, ...e.tags].join(' ').toLocaleLowerCase('de').includes(text)) return false;
  return true;
}

/** Angeheftet zuerst, dann zuletzt geaendert. */
export function filtern(eintraege: SammelEintrag[], f: SammelFilter): SammelEintrag[] {
  return eintraege
    .filter((e) => passtOhneHerkunft(e, f) && (!f.herkunft || e.herkunft === f.herkunft))
    .sort((a, b) => Number(b.angeheftet) - Number(a.angeheftet) || b.geaendert.localeCompare(a.geaendert));
}

/** Wie viele Eintraege je Herkunft zum uebrigen Filter passen. */
export function zaehleHerkunft(eintraege: SammelEintrag[], f: SammelFilter): Record<Herkunft, number> {
  const zahl = Object.fromEntries(HERKUNFT.map((h) => [h.key, 0])) as Record<Herkunft, number>;
  for (const e of eintraege) if (passtOhneHerkunft(e, f)) zahl[e.herkunft] += 1;
  return zahl;
}

export type ZuordnungsWahl = { art: 'bereich' | 'projekt' | 'ziel'; id: ID } | null;

/**
 * Die Felder zu einer Wahl nach der Regel aus F-11. `null`, wenn das gewaehlte
 * Projekt oder Ziel nicht (mehr) existiert.
 */
export function zuordnungFuer(wahl: ZuordnungsWahl, projects: Project[], goals: Goal[]): Zuordnung | null {
  if (!wahl) return { areaId: null };
  if (wahl.art === 'bereich') return { areaId: wahl.id };
  if (wahl.art === 'projekt') {
    const p = projects.find((x) => x.id === wahl.id);
    return p ? { areaId: p.areaId, projectId: p.id } : null;
  }
  const g = goals.find((x) => x.id === wahl.id);
  return g ? { areaId: g.areaId, goalId: g.id } : null;
}

/**
 * Der Bereich, in dem ein Eintrag gerade steht: der des Projekts bzw. Ziels,
 * falls sich dessen Bereich seit der Zuordnung geaendert hat.
 */
export function bereichVon(e: Zuordnung, projects: Map<ID, Project>, goals: Map<ID, Goal>): ID | null {
  if (e.projectId && projects.has(e.projectId)) return projects.get(e.projectId)!.areaId;
  if (e.goalId && goals.has(e.goalId)) return goals.get(e.goalId)!.areaId;
  return e.areaId ?? null;
}

/** Gehoert der Eintrag zu diesem Bereich, Projekt oder Ziel? */
export function verknuepftMit(
  e: Zuordnung,
  ziel: { art: 'bereich' | 'projekt' | 'ziel'; id: ID },
  projects: Map<ID, Project>,
  goals: Map<ID, Goal>,
): boolean {
  if (ziel.art === 'projekt') return e.projectId === ziel.id;
  if (ziel.art === 'ziel') return e.goalId === ziel.id;
  return bereichVon(e, projects, goals) === ziel.id;
}
